'use client';

import React from 'react';

/**
 * Isolates a single layout block so a render failure (e.g. malformed Lexical
 * rich text throwing #117 "Invalid indent value") degrades to nothing instead
 * of white-screening the entire published page. Every block in the catch-all
 * route is wrapped in this.
 *
 * In production the failed block renders nothing. In development it renders a
 * visible marker so the broken block is obvious while editing.
 */
interface Props {
  blockType?: string;
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
}

export class BlockErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // Surface the failure in logs without taking the page down.
    console.error(`[block:${this.props.blockType ?? 'unknown'}] render failed:`, error);
  }

  render() {
    if (this.state.hasError) {
      if (process.env.NODE_ENV !== 'production') {
        return (
          <div style={{ padding: '1rem', margin: '0.5rem', border: '1px dashed #dc2626', color: '#dc2626', fontSize: 14 }}>
            Block &quot;{this.props.blockType ?? 'unknown'}&quot; failed to render (see console). This is hidden in production.
          </div>
        );
      }
      return null;
    }
    return this.props.children;
  }
}
